/**
 * Trip Export Controller
 *
 * Handles complete trip export functionality as ZIP archives.
 * Coordinates with all trip data services to create comprehensive
 * export packages containing all trip-related information.
 *
 * Features:
 * - Complete trip data export (days, places, travel, accommodation, documents)
 * - Real binary ZIP archive generation using JSZip library
 * - Multiple export formats within the archive (JSON, CSV)
 * - Document file inclusion with proper organization
 * - Export metadata and validation
 */

// Import JSZip directly for ZIP generation
import JSZip from 'jszip';

/**
 * Sanitizes filename to remove forbidden characters (safety net for export)
 * Forbidden characters: : * ? " < > | \
 * @param {string} filename - Original filename
 * @returns {string} Sanitized filename safe for Windows ZIP extraction
 */
const sanitizeFilenameForExport = (filename) => {
  if (!filename || typeof filename !== 'string') {
    return 'file';
  }

  // Check if filename contains forbidden characters
  if (/[:*?"<>|\\]/.test(filename)) {
    console.warn(`[TripExport] Sanitizing filename with forbidden characters: "${filename}"`);
    return filename
      .replace(/[:*?"<>|\\]/g, '-')
      .replace(/\s+/g, ' ')
      .replace(/^[-\s]+|[-\s]+$/g, '')
      .trim() || 'file';
  }

  return filename;
};

/**
 * Ensures filename is unique within a set of existing filenames
 * Adds counter suffix if filename already exists
 * @param {string} filename - Base filename
 * @param {Set} usedFilenames - Set of already used filenames
 * @returns {string} Unique filename
 */
const ensureUniqueFilename = (filename, usedFilenames) => {
  if (!usedFilenames.has(filename)) {
    return filename;
  }

  // Split filename into base and extension
  const lastDotIndex = filename.lastIndexOf('.');
  const hasExtension = lastDotIndex > 0 && lastDotIndex < filename.length - 1;

  const base = hasExtension ? filename.substring(0, lastDotIndex) : filename;
  const ext = hasExtension ? filename.substring(lastDotIndex) : '';

  // Find unique name by adding counter
  let counter = 1;
  let uniqueName;
  do {
    uniqueName = `${base}-${counter}${ext}`;
    counter++;
  } while (usedFilenames.has(uniqueName));

  console.log(`[TripExport] Filename collision detected. "${filename}" → "${uniqueName}"`);
  return uniqueName;
};

// Import domain services asynchronously to get real Firebase data with graceful fallback
let daysService, placesService, accommodationService, travelService, documentsService;
let servicesLoaded = false;

// Async service loading function for ES6 modules
async function loadServices() {
  try {
    const daysModule = await import('@trip-planner-monorepo/days');
    daysService = daysModule.daysService;
  } catch (error) {
    console.warn('Days service not available for trip export:', error.message);
  }

  try {
    const placesModule = await import('@trip-planner-monorepo/places');
    placesService = placesModule.placesService;
  } catch (error) {
    console.warn('Places service not available for trip export:', error.message);
  }

  try {
    const accommodationModule = await import('@trip-planner-monorepo/accommodation');
    accommodationService = accommodationModule.accommodationService;
  } catch (error) {
    console.warn('Accommodation service not available for trip export:', error.message);
  }

  try {
    const travelModule = await import('@trip-planner-monorepo/travel');
    travelService = travelModule.travelService;
  } catch (error) {
    console.warn('Travel service not available for trip export:', error.message);
  }

  try {
    const documentsModule = await import('@trip-planner-monorepo/documents');
    documentsService = documentsModule.documentService;  // Fixed: documentService not documentsService
  } catch (error) {
    console.warn('Documents service not available for trip export:', error.message);
  }
}

class TripExportController {
  constructor() {
    this.exportFormats = ['zip'];
    this.supportedDataFormats = ['json', 'csv'];
    this.maxExportSize = 100 * 1024 * 1024; // 100MB limit

    // Progress tracking for UI feedback
    this.progressCallback = null;
    this.totalSteps = 5;
  }

  /**
   * Set progress callback for UI updates
   * @param {Function} callback - Function to call with progress updates
   */
  setProgressCallback(callback) {
    this.progressCallback = callback;
  }

  /**
   * Update progress indicator
   * @param {number} stepNumber - Current step (1-5)
   * @param {string} stepDescription - Description of current step
   */
  updateProgress(stepNumber, stepDescription) {
    if (this.progressCallback) {
      const progress = Math.round((stepNumber / this.totalSteps) * 100);
      this.progressCallback({
        progress,
        currentStep: stepDescription,
        currentStepNumber: stepNumber,
        totalSteps: this.totalSteps
      });
    }
  }

  /**
   * Export complete trip as ZIP archive
   *
   * @param {Object} context - Current application context
   * @param {string} context.tripId - Current trip ID
   * @param {Object} context.trip - Current trip data
   * @param {Object} context.user - Current user data
   * @param {Object} options - Export options
   * @returns {Promise<boolean>} Success status
   */
  async exportTrip(context, options = {}) {
    try {
      console.log('Starting complete trip export...');

      // Step 1: Validation
      this.updateProgress(1, 'Validating export settings...');

      // Validate context
      if (!this.validateExportContext(context)) {
        throw new Error('Invalid export context - missing required data');
      }

      // Show user feedback
      this.showExportStartedNotification();

      // Prepare export options
      const exportOptions = {
        includeDocuments: options.includeDocuments !== false,
        includeImages: options.includeImages !== false,
        exportFormat: options.exportFormat || 'json',
        ...options
      };

      // Step 2: Gather trip data
      this.updateProgress(2, 'Fetching trip data from Firebase...');
      const tripData = await this.gatherTripData(context);

      // Step 3: Validate data size
      this.updateProgress(3, 'Validating export size...');
      const estimatedSize = await this.estimateExportSize(tripData);
      if (estimatedSize > this.maxExportSize) {
        throw new Error(`Export too large (${Math.round(estimatedSize / 1024 / 1024)}MB). Maximum size is ${Math.round(this.maxExportSize / 1024 / 1024)}MB.`);
      }

      // Step 4: Fetch document files
      this.updateProgress(4, 'Fetching document files from storage...');

      // Step 5: Generate export package
      this.updateProgress(5, 'Creating ZIP archive...');
      await this.generateExportPackage(tripData, exportOptions, context);

      this.showExportCompletedNotification();
      return true;
    } catch (error) {
      console.error('Trip Export failed:', error);
      this.showExportErrorNotification(error.message);
      return false;
    }
  }

  /**
   * Gather all trip-related data for export
   *
   * @param {Object} context - Current application context
   * @returns {Promise<Object>} Complete trip data
   */
  async gatherTripData(context) {
    try {
      console.log('[Trip Export] Gathering trip data from Firebase...');

      // Ensure services are loaded before attempting to use them
      if (!servicesLoaded) {
        await loadServices();
        servicesLoaded = true;
      }

      // Fetch real Firebase data from each service
      console.log('[Trip Export] Fetching data from Firebase services...');

      const days = await daysService?.fetchDays(context.effectiveUserId || context.user.uid, context.tripId) || [];
      console.log(`[Trip Export] Fetched ${days.length} days`);
      if (days.length > 0) {
        console.log('[Trip Export] Sample day data:', {
          hasContent: !!days[0].content,
          hasContentBlocks: !!days[0].contentBlocks,
          contentLength: days[0].content?.length,
          contentBlocksLength: days[0].contentBlocks?.length,
          allKeys: Object.keys(days[0])
        });
      }

      const places = await placesService?.fetchPlaces(context.effectiveUserId || context.user.uid, context.tripId) || [];
      console.log(`[Trip Export] Fetched ${places.length} places`);
      if (places.length > 0) {
        console.log('[Trip Export] Sample place data:', {
          hasContentBlocks: !!places[0].contentBlocks,
          contentBlocksLength: places[0].contentBlocks?.length,
          allKeys: Object.keys(places[0])
        });
      }

      const accommodation = await accommodationService?.fetchAccommodation(context.effectiveUserId || context.user.uid, context.tripId) || [];
      console.log(`[Trip Export] Fetched ${accommodation.length} accommodation items`);
      if (accommodation.length > 0) {
        console.log('[Trip Export] Sample accommodation data:', {
          hasContentBlocks: !!accommodation[0].contentBlocks,
          contentBlocksLength: accommodation[0].contentBlocks?.length,
          allKeys: Object.keys(accommodation[0])
        });
      }

      const travel = await travelService?.fetchTravel(context.effectiveUserId || context.user.uid, context.tripId) || [];
      console.log(`[Trip Export] Fetched ${travel.length} travel items`);
      if (travel.length > 0) {
        console.log('[Trip Export] Sample travel data:', {
          hasContentBlocks: !!travel[0].contentBlocks,
          contentBlocksLength: travel[0].contentBlocks?.length,
          allKeys: Object.keys(travel[0])
        });
      }

      const documents = await documentsService?.fetchDocuments(context.effectiveUserId || context.user.uid, context.tripId) || [];
      console.log(`[Trip Export] Fetched ${documents.length} documents`);

      let links = [];
      let linkRegistry = null;
      try {
        const shared = await import('@trip-planner-monorepo/shared');
        links = await shared.connectionRegistryService.getAllLinks(
          context.effectiveUserId || context.user.uid,
          context.tripId,
          { includeArchived: true }
        );
        linkRegistry = await shared.linkRegistryConfigService.getLinkRegistryConfig(
          context.effectiveUserId || context.user.uid,
          context.tripId
        );
        console.log(`[Trip Export] Fetched ${links.length} connection registry links`);
      } catch (linkError) {
        console.warn('[Trip Export] Connection registry data not available:', linkError.message);
      }

      console.log(`[Trip Export] Data fetched - Days: ${days.length}, Places: ${places.length}, Accommodation: ${accommodation.length}, Travel: ${travel.length}, Documents: ${documents.length}, Links: ${links.length}`);

      // Transform Firebase data to export format
      const tripData = {
        // Trip metadata
        trip: {
          id: context.tripId,
          name: context.trip?.name || 'My Trip',
          description: context.trip?.description || '',
          startDate: context.trip?.startDate || this.extractStartDate(days),
          endDate: context.trip?.endDate || this.extractEndDate(days),
          destination: context.trip?.destination || this.extractDestination(places, accommodation),
          travelers: context.trip?.travelers || 1,
          budget: context.trip?.budget || 0,
          currency: context.trip?.currency || 'USD',
          status: context.trip?.status || 'planned',
          createdAt: context.trip?.createdAt || new Date().toISOString(),
          updatedAt: new Date().toISOString()
        },

        // Transform days data
        days: this.transformDaysData(days),

        // Transform places data
        places: this.transformPlacesData(places),

        // Transform accommodation data
        accommodation: this.transformAccommodationData(accommodation),

        // Transform travel data
        travel: this.transformTravelData(travel),

        // Transform documents data
        documents: this.transformDocumentsData(documents),

        // Connection registry (Phase 2+)
        links: links || [],
        _meta: linkRegistry ? { linkRegistry } : undefined,

        // Export metadata
        exportMetadata: {
          exportDate: new Date().toISOString(),
          exportVersion: '1.0.0',
          exportedBy: context.user.email,
          tripId: context.tripId,
          dataVersion: '2025.1',
          totalItems: 0, // Will be calculated
          fileCount: 0   // Will be calculated
        }
      };

      // Calculate totals
      tripData.exportMetadata.totalItems =
        tripData.days.length +
        tripData.places.length +
        tripData.accommodation.length +
        tripData.travel.length +
        tripData.documents.length +
        (tripData.links?.length || 0);

      tripData.exportMetadata.fileCount = tripData.documents.length;

      console.log('[Trip Export] Trip data gathered successfully:', {
        totalItems: tripData.exportMetadata.totalItems,
        fileCount: tripData.exportMetadata.fileCount
      });

      return tripData;
    } catch (error) {
      console.error('[Trip Export] Error gathering trip data:', error);
      throw error;
    }
  }

  /**
   * Transform days data from Firebase to export format
   * CRITICAL: Preserve ALL original fields including content/contentBlocks arrays
   *
   * @param {Array} days - Days data from Firebase
   * @returns {Array} Transformed days data with ALL original fields preserved
   */
  transformDaysData(days) {
    if (!days || days.length === 0) {
      console.warn('[Trip Export] No days to transform');
      return [];
    }

    return days.map((day, index) => ({
      ...day,  // Preserve ALL original Firebase fields including content/contentBlocks
      // Only add computed fields if they don't exist
      dayNumber: day.dayNumber || (index + 1),
      title: day.title || `Day ${day.dayNumber || (index + 1)}`,
      // Add activities for convenience (but keep original content/contentBlocks)
      activities: this.extractActivities(day.content)
    }));
  }

  /**
   * Transform places data from Firebase to export format
   * CRITICAL: Preserve ALL original fields including contentBlocks arrays
   *
   * @param {Array} places - Places data from Firebase
   * @returns {Array} Transformed places data with ALL original fields preserved
   */
  transformPlacesData(places) {
    if (!places || places.length === 0) {
      console.warn('[Trip Export] No places to transform');
      return [];
    }

    return places.map(place => ({
      ...place,  // Preserve ALL original Firebase fields including contentBlocks
      // Only add default values for missing fields
      name: place.name || 'Unnamed Place',
      type: place.type || 'other'
    }));
  }

  /**
   * Transform accommodation data from Firebase to export format
   * CRITICAL: Preserve ALL original fields including contentBlocks arrays
   *
   * @param {Array} accommodation - Accommodation data from Firebase
   * @returns {Array} Transformed accommodation data with ALL original fields preserved
   */
  transformAccommodationData(accommodation) {
    if (!accommodation || accommodation.length === 0) {
      console.warn('[Trip Export] No accommodation to transform');
      return [];
    }

    return accommodation.map(item => ({
      ...item,  // Preserve ALL original Firebase fields including contentBlocks
      // Only add default values for missing fields
      type: item.type || 'hotel',
      // Add convenience name field if not present
      name: item.name || item.details?.name || item.summary || 'Unnamed Accommodation'
    }));
  }

  /**
   * Transform travel data from Firebase to export format
   * CRITICAL: Preserve ALL original fields including contentBlocks arrays
   *
   * @param {Array} travel - Travel data from Firebase
   * @returns {Array} Transformed travel data with ALL original fields preserved
   */
  transformTravelData(travel) {
    if (!travel || travel.length === 0) {
      console.warn('[Trip Export] No travel to transform');
      return [];
    }

    return travel.map(item => ({
      ...item,  // Preserve ALL original Firebase fields including contentBlocks
      // Only add default values for missing fields
      type: item.type || 'other'
    }));
  }

  /**
   * Transform documents data from Firebase to export format
   * CRITICAL: Preserve ALL original fields including metadata
   *
   * @param {Array} documents - Documents data from Firebase
   * @returns {Array} Transformed documents data with ALL original fields preserved
   */
  transformDocumentsData(documents) {
    if (!documents || documents.length === 0) {
      console.warn('[Trip Export] No documents to transform');
      return [];
    }

    return documents.map(doc => ({
      ...doc,  // Preserve ALL original Firebase fields
      // Only add default values for missing fields
      name: doc.name || doc.fileName || 'Unnamed Document',
      type: doc.type || 'other'
    }));
  }

  /**
   * Extract activities from day's content blocks
   *
   * @param {Array} content - Day's content array
   * @returns {Array} Activities as array of strings
   */
  extractActivities(content) {
    if (!content || !Array.isArray(content)) {
      return [];
    }

    const activities = [];

    content.forEach(block => {
      if (block.type === 'text' && block.text) {
        activities.push(block.text);
      } else if (block.type === 'accommodation_reference' && block.displayText) {
        activities.push(block.displayText);
      } else if (block.type === 'travel_reference' && block.displayText) {
        activities.push(block.displayText);
      } else if (block.type === 'place_reference' && block.displayText) {
        activities.push(block.displayText);
      }
    });

    return activities;
  }

  /**
   * Extract start date from days data
   *
   * @param {Array} days - Days data
   * @returns {string} Start date
   */
  extractStartDate(days) {
    if (!days || days.length === 0) {
      return '';
    }

    const sortedDays = [...days].sort((a, b) => {
      const dateA = new Date(a.date);
      const dateB = new Date(b.date);
      return dateA - dateB;
    });

    return sortedDays[0]?.date || '';
  }

  /**
   * Extract end date from days data
   *
   * @param {Array} days - Days data
   * @returns {string} End date
   */
  extractEndDate(days) {
    if (!days || days.length === 0) {
      return '';
    }

    const sortedDays = [...days].sort((a, b) => {
      const dateA = new Date(a.date);
      const dateB = new Date(b.date);
      return dateA - dateB;
    });

    return sortedDays[sortedDays.length - 1]?.date || '';
  }

  /**
   * Extract destination from places or accommodation data
   *
   * @param {Array} places - Places data
   * @param {Array} accommodation - Accommodation data
   * @returns {string} Destination description
   */
  extractDestination(places, accommodation) {
    // Try to get from first place
    if (places && places.length > 0) {
      const firstPlace = places[0];
      if (firstPlace.city && firstPlace.country) {
        return `${firstPlace.city}, ${firstPlace.country}`;
      }
      if (firstPlace.address) {
        return firstPlace.address;
      }
    }

    // Try to get from first accommodation
    if (accommodation && accommodation.length > 0) {
      const firstAccom = accommodation[0];
      const details = firstAccom.details || {};
      if (details.city && details.country) {
        return `${details.city}, ${details.country}`;
      }
      if (details.address) {
        return details.address;
      }
    }

    return 'Unknown Destination';
  }

  /**
   * Generate the export package as a real binary ZIP file
   *
   * @param {Object} tripData - Complete trip data
   * @param {Object} options - Export options
   * @param {Object} context - Application context
   */
  async generateExportPackage(tripData, options, context) {
    console.log('[TripExport] Generating binary ZIP export package...');

    try {
      // Create new JSZip instance
      const zip = new JSZip();

      // Create folder structure
      const tripDataFolder = zip.folder('trip-data');
      const exportsFolder = zip.folder('exports');
      const documentsFolder = zip.folder('documents');
      const mediaFolder = zip.folder('media');

      // Add trip data as JSON files
      tripDataFolder.file('trip-info.json', JSON.stringify(tripData.trip, null, 2));
      tripDataFolder.file('days.json', JSON.stringify(tripData.days || [], null, 2));
      tripDataFolder.file('places.json', JSON.stringify(tripData.places || [], null, 2));
      tripDataFolder.file('accommodation.json', JSON.stringify(tripData.accommodation || [], null, 2));
      tripDataFolder.file('travel.json', JSON.stringify(tripData.travel || [], null, 2));
      tripDataFolder.file('documents.json', JSON.stringify(tripData.documents || [], null, 2));
      if (tripData.links) {
        tripDataFolder.file('links.json', JSON.stringify(tripData.links || [], null, 2));
      }
      if (tripData._meta?.linkRegistry) {
        tripDataFolder.file('link-registry-config.json', JSON.stringify(tripData._meta.linkRegistry, null, 2));
      }

      // Add CSV exports
      if (tripData.days && tripData.days.length > 0) {
        exportsFolder.file('itinerary.csv', this.generateItineraryCSV(tripData));
      }
      if (tripData.places && tripData.places.length > 0) {
        exportsFolder.file('places.csv', this.generatePlacesCSV(tripData.places));
      }

      // Fetch and add actual document binary files from Firebase Storage
      let storageFiles = [];
      if (options.includeDocuments && tripData.documents && tripData.documents.length > 0) {
        console.log('[TripExport] Fetching document storage files from Firebase...');
        try {
          // Import necessary Firebase modules
          const { db, storage } = await import('@trip-planner-monorepo/shared');
          const { collection, getDocs } = await import('firebase/firestore');
          const { ref: storageRef, getBlob: _storageGetBlob } = await import('firebase/storage');

          console.log('[TripExport] Fetching documents from Firestore...');
          const documentsRef = collection(db, 'users', context.effectiveUserId || context.user.uid, 'trips', context.tripId, 'documents');
          const snapshot = await getDocs(documentsRef);

          console.log(`[TripExport] Found ${snapshot.docs.length} document records`);

          // Fetch document files using Firebase Storage SDK (bypasses CORS)
          for (const docSnapshot of snapshot.docs) {
            const docData = docSnapshot.data();

            if (docData.storagePath) {
              try {
                console.log(`[TripExport] Fetching file: ${docData.name} from storage path: ${docData.storagePath}`);

                // Use Firebase Storage SDK getBytes instead of fetch (no CORS issues)
                const fileRef = storageRef(storage, docData.storagePath);
                const { getBytes } = await import('firebase/storage');

                // Download file as bytes
                const bytes = await getBytes(fileRef);

                // Convert bytes to blob
                const blob = new Blob([bytes], { type: docData.mimeType || 'application/octet-stream' });

                // Add to storage files array
                storageFiles.push({
                  name: docData.storagePath,
                  blob: blob,
                  documentId: docSnapshot.id,
                  originalName: docData.name || docData.originalName
                });

                console.log(`[TripExport] Fetched storage file: ${docData.name} (${blob.size} bytes)`);
              } catch (fileError) {
                console.error(`[TripExport] Error fetching file ${docData.name}:`, fileError);
                console.error('[TripExport] Error details:', fileError.message);
                // Continue with other files even if one fails
              }
            } else {
              console.log(`[TripExport] Skipping document without storagePath: ${docData.name}`);
            }
          }

          console.log(`[TripExport] Fetched ${storageFiles.length} storage files`);
        } catch (storageError) {
          console.error('[TripExport] Error fetching storage files:', storageError);
          // Continue export without storage files
        }
      }

      // Add document files to appropriate folders (documents vs media)
      await this.addDocumentFilesToZip(
        documentsFolder,
        mediaFolder,
        tripData.documents || [],
        storageFiles
      );

      // Add README and manifest
      zip.file('README.txt', this.generateReadmeContent(tripData));
      zip.file('export-manifest.json', JSON.stringify(tripData.exportMetadata, null, 2));

      // Generate binary ZIP blob
      console.log('[TripExport] Generating binary ZIP blob with JSZip...');
      const zipBlob = await zip.generateAsync({
        type: 'blob',
        compression: 'DEFLATE',
        compressionOptions: { level: 6 }
      });

      // Verify blob generation
      console.log('[TripExport] ZIP blob generated:', {
        type: zipBlob.type,
        size: zipBlob.size,
        isBlob: zipBlob instanceof Blob
      });

      if (!zipBlob || zipBlob.size === 0) {
        throw new Error('ZIP generation failed - empty blob returned');
      }

      // Generate filename
      const filename = this.generateExportFilename(tripData.trip);
      console.log('[TripExport] Filename:', filename);

      // Download the binary ZIP file
      this.downloadZipFile(zipBlob, filename);

      console.log('[TripExport] ZIP export completed successfully');
    } catch (error) {
      console.error('[TripExport] ZIP generation failed:', error);
      throw new Error(`Failed to create ZIP export: ${error.message}`);
    }
  }

  /**
   * Add document files to ZIP folders (documents vs media)
   * Routes files based on MIME type (images go to media, others to documents)
   */
  async addDocumentFilesToZip(documentsFolder, mediaFolder, documents, storageFiles) {
    console.log(`[TripExport] Adding ${documents.length} documents and ${storageFiles.length} storage files to ZIP`);

    let documentsAdded = 0;
    let mediaAdded = 0;
    let errors = 0;

    // Track used filenames to prevent collisions
    const usedDocumentFilenames = new Set();
    const usedMediaFilenames = new Set();

    // Process storage files fetched from Firebase Storage
    if (storageFiles && storageFiles.length > 0) {
      for (const fileData of storageFiles) {
        try {
          const isImage = this.isImageFile(fileData);
          const folder = isImage ? mediaFolder : documentsFolder;
          const usedFilenames = isImage ? usedMediaFilenames : usedDocumentFilenames;

          const rawFileName = fileData.originalName || fileData.name;
          const sanitizedFileName = sanitizeFilenameForExport(rawFileName);
          const fileName = ensureUniqueFilename(sanitizedFileName, usedFilenames);

          // Track this filename as used
          usedFilenames.add(fileName);

          console.log(`[TripExport] Adding ${isImage ? 'image' : 'document'}: ${fileName} (${fileData.blob.size} bytes)`);

          // Add blob to appropriate folder
          folder.file(fileName, fileData.blob);

          if (isImage) {
            mediaAdded++;
          } else {
            documentsAdded++;
          }
        } catch (error) {
          console.error(`[TripExport] Error adding file ${fileData.originalName}:`, error);
          errors++;
        }
      }
    }

    // Create README files in each folder
    if (documentsAdded > 0) {
      documentsFolder.file('README.txt',
        'Document Files\n\n' +
        'This folder contains your trip-related document files (PDFs, text files, etc.).\n\n' +
        `Total documents included: ${documentsAdded}\n\n` +
        'Files:\n' +
        documents
          .filter(doc => !this.isImageFileByMimeType(doc.mimeType))
          .map(doc => `- ${doc.name}`)
          .join('\n')
      );
    } else {
      documentsFolder.file('README.txt',
        'Document Files\n\n' +
        'Binary document files are not included in this export due to Firebase Storage CORS restrictions.\n\n' +
        'Document metadata and previews are available in trip-data/documents.json.\n\n' +
        'To enable document file export:\n' +
        '1. Configure CORS on your Firebase Storage bucket\n' +
        '2. Use Google Cloud CLI: gsutil cors set cors.json gs://your-bucket\n' +
        '3. Or deploy the app to a production domain (CORS not needed)\n\n' +
        'Documents found but not exported:\n' +
        documents.map(doc => `- ${doc.name}`).join('\n')
      );
    }

    if (mediaAdded > 0) {
      mediaFolder.file('README.txt',
        'Media Files\n\n' +
        'This folder contains images and media files associated with your trip.\n\n' +
        `Total media files included: ${mediaAdded}\n\n` +
        'Files:\n' +
        documents
          .filter(doc => this.isImageFileByMimeType(doc.mimeType))
          .map(doc => `- ${doc.name}`)
          .join('\n')
      );
    } else {
      const imageDocuments = documents.filter(doc => this.isImageFileByMimeType(doc.mimeType));
      mediaFolder.file('README.txt',
        'Media Files\n\n' +
        'Binary media files are not included in this export due to Firebase Storage CORS restrictions.\n\n' +
        'Image metadata and base64 previews are available in trip-data/documents.json.\n\n' +
        'To enable media file export:\n' +
        '1. Configure CORS on your Firebase Storage bucket\n' +
        '2. Use Google Cloud CLI: gsutil cors set cors.json gs://your-bucket\n' +
        '3. Or deploy the app to a production domain (CORS not needed)\n\n' +
        (imageDocuments.length > 0
          ? 'Images found but not exported:\n' + imageDocuments.map(doc => `- ${doc.name}`).join('\n')
          : 'No image files found in this trip.'
        )
      );
    }

    console.log(`[TripExport] File addition complete: ${documentsAdded} documents, ${mediaAdded} media, ${errors} errors`);
  }

  /**
   * Check if file data is an image based on blob or metadata
   */
  isImageFile(fileData) {
    // Check blob type first
    if (fileData.blob && fileData.blob.type) {
      return fileData.blob.type.startsWith('image/');
    }

    // Fallback to file name extension
    const name = fileData.originalName || fileData.name || '';
    const ext = name.toLowerCase().split('.').pop();
    return ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg'].includes(ext);
  }

  /**
   * Check if MIME type is an image
   */
  isImageFileByMimeType(mimeType) {
    return mimeType && mimeType.startsWith('image/');
  }

  /**
   * Generate CSV content for itinerary
   */
  generateItineraryCSV(tripData) {
    const headers = ['Day', 'Date', 'Title', 'Activities', 'Notes', 'Budget'];
    let csv = headers.join(',') + '\n';

    if (tripData.days && tripData.days.length > 0) {
      tripData.days.forEach(day => {
        const activitiesStr = Array.isArray(day.activities) ? day.activities.join('; ') : (day.activities || '');
        const row = [
          day.dayNumber || '',
          day.date || '',
          `"${day.title || ''}"`,
          `"${activitiesStr}"`,
          `"${day.notes || ''}"`,
          day.budget || 0
        ];
        csv += row.join(',') + '\n';
      });
    }

    return csv;
  }

  /**
   * Generate CSV content for places
   */
  generatePlacesCSV(places) {
    const headers = ['Name', 'Type', 'Address', 'Visit Date', 'Duration', 'Notes'];
    let csv = headers.join(',') + '\n';

    if (places && places.length > 0) {
      places.forEach(place => {
        const row = [
          `"${place.name || ''}"`,
          place.type || '',
          `"${place.address || ''}"`,
          place.visitDate || '',
          place.duration || '',
          `"${place.notes || ''}"`
        ];
        csv += row.join(',') + '\n';
      });
    }

    return csv;
  }

  /**
   * Generate CSV content for travel
   */
  generateTravelCSV(travel) {
    const headers = ['Type', 'Details', 'Departure', 'Arrival', 'Cost'];
    let csv = headers.join(',') + '\n';

    if (travel && travel.length > 0) {
      travel.forEach(trip => {
        let details = '';
        let departure = '';
        let arrival = '';
        let cost = '';

        if (trip.type === 'flight') {
          details = `${trip.airline || ''} ${trip.flightNumber || ''}`.trim();
          departure = `${trip.departure?.city || ''} ${trip.departure?.date || ''} ${trip.departure?.time || ''}`.trim();
          arrival = `${trip.arrival?.city || ''} ${trip.arrival?.date || ''} ${trip.arrival?.time || ''}`.trim();
          cost = `${trip.cost || 0} ${trip.currency || 'USD'}`;
        } else if (trip.type === 'train') {
          details = `${trip.operator || ''} ${trip.trainNumber || ''}`.trim();
          departure = `${trip.departure?.city || ''} ${trip.departure?.date || ''} ${trip.departure?.time || ''}`.trim();
          arrival = `${trip.arrival?.city || ''} ${trip.arrival?.date || ''} ${trip.arrival?.time || ''}`.trim();
          cost = `${trip.cost || 0} ${trip.currency || 'USD'}`;
        } else if (trip.type === 'bus') {
          details = `${trip.operator || ''} ${trip.busNumber || ''}`.trim();
          departure = `${trip.departure?.city || ''} ${trip.departure?.date || ''} ${trip.departure?.time || ''}`.trim();
          arrival = `${trip.arrival?.city || ''} ${trip.arrival?.date || ''} ${trip.arrival?.time || ''}`.trim();
          cost = `${trip.cost || 0} ${trip.currency || 'USD'}`;
        } else {
          details = trip.summary || '';
        }

        const row = [
          trip.type || '',
          `"${details}"`,
          `"${departure}"`,
          `"${arrival}"`,
          cost
        ];
        csv += row.join(',') + '\n';
      });
    }

    return csv;
  }

  /**
   * Generate README content for the export
   */
  generateReadmeContent(tripData) {
    return `# Trip Export: ${tripData.trip.name}

## Export Information
- Export Date: ${tripData.exportMetadata.exportDate}
- Export Version: ${tripData.exportMetadata.exportVersion}
- Trip ID: ${tripData.trip.id}
- Total Items: ${tripData.exportMetadata.totalItems}

## Folder Structure
- \`trip-data/\` - Complete trip data in JSON format
- \`trip-data/links.json\` - Connection registry links (entity relationships)
- \`trip-data/link-registry-config.json\` - Link registry read/write mode for this trip
- \`exports/\` - CSV exports for easy viewing
- \`documents/\` - Trip-related document files
- \`export-manifest.json\` - Export metadata and validation

## Trip Overview
- **Destination**: ${tripData.trip.destination}
- **Dates**: ${tripData.trip.startDate} to ${tripData.trip.endDate}
- **Days**: ${tripData.days.length}
- **Places**: ${tripData.places.length}
- **Accommodations**: ${tripData.accommodation.length}
- **Travel Arrangements**: ${tripData.travel.length}
- **Documents**: ${tripData.documents.length}
- **Links**: ${(tripData.links || []).length}

## How to Use This Export
1. The \`trip-data/\` folder contains all your trip information in JSON format
2. The \`exports/\` folder has CSV files you can open in Excel or Google Sheets
3. The \`documents/\` folder contains your uploaded trip documents
4. Use \`export-manifest.json\` to verify the export completeness

Generated by Trip Planner - https://tripplanner.example.com`;
  }

  /**
   * Download binary ZIP file to user's computer
   *
   * @param {Blob} zipBlob - Binary ZIP blob from ZipFormatProcessor
   * @param {string} filename - Filename without extension
   */
  downloadZipFile(zipBlob, filename) {
    console.log('[TripExport] Initiating ZIP download:', {
      filename: `${filename}.zip`,
      size: zipBlob.size,
      type: zipBlob.type
    });

    // Create download URL from blob
    const url = window.URL.createObjectURL(zipBlob);

    // Create temporary anchor element for download
    const a = document.createElement('a');
    a.href = url;
    a.download = `${filename}.zip`;
    document.body.appendChild(a);
    a.click();

    // Cleanup
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);

    console.log('[TripExport] ZIP download initiated successfully');
  }

  // Helper methods

  validateExportContext(context) {
    return !!(context.user && context.tripId && context.trip);
  }

  async estimateExportSize(tripData) {
    // Rough estimation based on data structure size
    const jsonSize = JSON.stringify(tripData).length;
    const documentSize = tripData.documents.reduce((sum, doc) => sum + (doc.size || 0), 0);
    return jsonSize + documentSize + 10000; // Add overhead
  }

  generateExportFilename(trip) {
    const tripName = (trip.name || 'trip').replace(/[^a-zA-Z0-9]/g, '-');
    const date = new Date().toISOString().split('T')[0];
    return `${tripName}-export-${date}`;
  }


  // Notification methods

  showExportStartedNotification() {
    console.log('Starting complete trip export...');
  }

  showExportCompletedNotification() {
    console.log('Trip export completed successfully');
  }

  showExportErrorNotification(errorMessage) {
    console.error('Trip export error:', errorMessage);
  }

  /**
   * Show trip export modal with confirmation
   *
   * @param {Object} context - Current application context
   * @returns {Promise<void>} Modal display (async)
   */
  async showTripExportModal(context) {
    try {
      // Import the modal component dynamically
      const { TripExportModal } = await import('@trip-planner-monorepo/app-features');

      // Create and show modal
      return this.renderModal(TripExportModal, context);
    } catch (error) {
      console.error('Failed to load trip export modal:', error);
      // Fallback to direct export
      return await this.exportTrip(context);
    }
  }

  /**
   * Render modal component (to be implemented by framework integration)
   *
   * @param {Component} ModalComponent - React modal component
   * @param {Object} context - Application context
   * @returns {Promise<void>} Modal rendering promise
   */
  async renderModal(ModalComponent, context) {
    // This method should be implemented by the framework integration
    console.log('Rendering TripExportModal with context:', {
      tripId: context.tripId,
      tripName: context.trip?.name,
      hasData: !!(context.trip && context.user)
    });

    // In a real integration, this would:
    // 1. Create a modal instance with React.createElement
    // 2. Render it using ReactDOM or framework renderer
    // 3. Pass context as props
    // 4. Handle modal events (export, close)
    // 5. Return a promise that resolves when modal closes

    // For now, fallback to direct export
    return await this.exportTrip(context);
  }

  /**
   * Check if export is available for current context
   */
  isExportAvailable(context) {
    return this.validateExportContext(context);
  }

  /**
   * Get export size estimate for user
   */
  async getExportSizeEstimate(context) {
    try {
      const tripData = await this.gatherTripData(context);
      const size = await this.estimateExportSize(tripData);
      return Math.round(size / 1024); // Return in KB
    } catch (error) {
      console.error('Failed to estimate export size:', error);
      return 0;
    }
  }
}

// Export singleton instance
export const tripExportController = new TripExportController();
export default tripExportController;